import { useMutation, useQueryClient } from "@tanstack/react-query";

import { IUpdateSurveyAnswerRequest } from "../../../@types/admin/AdminSurvey";
import { ISurveySyncResult } from "../../../@types/admin/AdminSurveySync";
import { surveysService, surveysSyncService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import {
  SURVEY_ANSWER_QUERY_KEY,
  surveysSyncComparisonKey,
} from "../../queries/admin/surveys";

interface IUpdateSurveyAnswerArgs {
  surveyAnswerId: string;
  request: IUpdateSurveyAnswerRequest;
}

/** ⚠️ Writes a submitted patient survey answer. Audited server-side. */
export function useUpdateSurveyAnswer() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    void,
    Error,
    IUpdateSurveyAnswerArgs
  >({
    mutationFn: async ({ surveyAnswerId, request }) =>
      ensureOk(
        await surveysService.updateSurveyAnswer(surveyAnswerId, request)
      ),
    onSuccess: (_data, { surveyAnswerId }) => {
      queryClient.invalidateQueries({
        queryKey: [SURVEY_ANSWER_QUERY_KEY, surveyAnswerId],
      });
      // The response list shows the answers, so it is stale too.
      queryClient.invalidateQueries({
        queryKey: ["admin-completed-responses"],
      });
      // ...and the audit trail has a new entry.
      queryClient.invalidateQueries({
        queryKey: ["admin-survey-answer-audit", surveyAnswerId],
      });
    },
  });

  return { updateSurveyAnswer: mutateAsync, isUpdatingAnswer: isPending };
}

/** Copies this region's missing surveys into Global (EN-5261). */
export function useSyncSurveys() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<ISurveySyncResult, Error>({
    mutationFn: async () => ensureOk(await surveysSyncService.sync()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: surveysSyncComparisonKey });
    },
  });

  return { syncSurveys: mutateAsync, isSyncing: isPending };
}
